// AF-3.19 P1 — Creator chain: Distribution. Real route (AF-3.7), real
// field names verified against creator_distribution.py's document
// construction (channel, platform, distribution_mode, governance_status,
// citation_class/crawlability/indexability/ai_accessibility, readiness,
// approval_status, permission_status). This page never renders a publish/
// send/execute control — status only, matching the same safety
// convention as CreatorCommandCenterPage's "READY FOR AUTHORIZED
// DISTRIBUTION EXECUTION" plain-text pattern.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, GovernanceBadge, GovernanceNote, ApprovalBadge, ReadinessBadge } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creatorDistributionAPI } from '@/lib/intelligenceApi';
import { CaretRight } from '@phosphor-icons/react';

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper, shared shape with the other Creator
// chain pages, so this page's position in the sequence is obvious at a
// glance.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

export default function CreatorDistributionPage() {
  const ctx = useBrandContext();
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    creatorDistributionAPI.list(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setTargets(r.data?.targets || r.data || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-distribution-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Distribution</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Distribution targets and their governance/readiness state. Status text only — this page never triggers publishing.</p>
        </div>

        <ChainProgress current="Distribution" />

        <BrandContextBar ctx={ctx} />

        {!ctx.ready && !ctx.loadingClients && <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its distribution targets." />}
        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && ctx.ready && targets.length === 0 && (
          <EmptyState title="No distribution target selected yet" description="Targets are evaluated once a creative decision exists for this entity." />
        )}

        {!loading && !error && targets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="creator-distribution-list">
            {targets.map((t) => (
              <div key={t.id} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="creator-distribution-row">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">{t.channel} · {t.platform}</p>
                    <p className="text-[12px] text-[var(--text-primary)]">{t.distribution_mode?.replace(/_/g, ' ') || '—'}</p>
                  </div>
                  <StatusBadge value={t.status} />
                </div>

                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <ReadinessBadge value={t.readiness} />
                  {t.approval_required && <ApprovalBadge value={t.approval_status || 'PENDING'} />}
                </div>

                {t.governance_status && (
                  <div className="mb-2">
                    <GovernanceBadge value={t.governance_status} />
                    <GovernanceNote value={t.governance_status} />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  {t.citation_class && <div><p className="text-[var(--text-tertiary)] font-mono uppercase">Citation class</p><p className="text-[var(--text-primary)]">{t.citation_class}</p></div>}
                  {t.crawlability && <div><p className="text-[var(--text-tertiary)] font-mono uppercase">Crawlability</p><StatusBadge value={t.crawlability} /></div>}
                  {t.indexability && <div><p className="text-[var(--text-tertiary)] font-mono uppercase">Indexability</p><StatusBadge value={t.indexability} /></div>}
                  {t.ai_accessibility && <div><p className="text-[var(--text-tertiary)] font-mono uppercase">AI accessibility</p><StatusBadge value={t.ai_accessibility} /></div>}
                </div>

                {t.distribution_fit !== undefined && t.distribution_fit !== null && (
                  <p className="text-[10px] text-[var(--text-secondary)] mt-2">Distribution fit: <span className="text-[var(--text-primary)] font-mono">{t.distribution_fit}</span></p>
                )}
                {t.is_primary && <p className="text-[10px] text-[var(--accent)] mt-1 font-mono uppercase">Primary target</p>}
                <button data-testid="creator-distribution-evidence-trigger" className="mt-2 text-[11px] text-[var(--accent)] block" onClick={() => setEvidenceFor(t)}>
                  Why this target? →
                </button>
              </div>
            ))}
          </div>
        )}

        {evidenceFor && (
          <EvidenceDrawer
            open={!!evidenceFor}
            onOpenChange={(open) => !open && setEvidenceFor(null)}
            title="Why Martian selected this distribution target"
            evidence={[]}
            provenance={{
              decision_id: evidenceFor.decision_id ? [evidenceFor.decision_id] : [],
              strategy_id: evidenceFor.strategy_id ? [evidenceFor.strategy_id] : [],
              artifact_id: evidenceFor.artifact_id ? [evidenceFor.artifact_id] : [],
              media_asset_id: evidenceFor.media_asset_id ? [evidenceFor.media_asset_id] : [],
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
